"use client";

import { ArrowUpRight, ChevronLeft, ChevronRight, Clock3 } from "lucide-react";
import { useEffect, useState } from "react";
import { InsightFilter } from "../../components/insight-filter";
import { CTA, PageHero, SectionHeading } from "../../components/site-shell";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "../../components/ui/pagination";
import { Spinner } from "../../components/ui/spinner";
import { createMetadata } from "../../lib/seo";
import type { ArticleListing } from "../../lib/we0-article-store";

const PAGE_SIZE = 5;

export const metadata = createMetadata({
  title: "品牌增长与SEO/GEO洞察",
  description:
    "分享企业品牌增长、产品出海、官网与独立站、内容营销、SEO 与 GEO 优化方法，帮助企业持续积累搜索曝光、品牌资产与客户渠道。",
  path: "/insights",
  keywords: [
    "品牌增长方法",
    "企业品牌营销",
    "产品出海营销",
    "海外营销策略",
    "SEO优化",
    "Google SEO",
    "GEO优化",
    "生成式引擎优化",
    "AI搜索优化",
    "企业内容营销",
    "内容资产运营",
    "海外独立站运营",
    "企业官网增长",
    "自然流量增长",
    "增长洞察",
  ],
  image: "/assets/SEO与GEO优化图.png",
});

export default function InsightsPage({
  publishedInsights,
}: {
  publishedInsights: ArticleListing[];
}) {
  const [insights, setInsights] = useState(publishedInsights);
  const [isLoading, setIsLoading] = useState(publishedInsights.length === 0);
  const [currentPage, setCurrentPage] = useState(1);
  const featuredInsight = insights[0];
  const totalPages = Math.max(1, Math.ceil(insights.length / PAGE_SIZE));
  const visibleInsights = insights.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  useEffect(() => {
    setCurrentPage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  useEffect(() => {
    let active = true;
    let loadingTimer: number | undefined;

    const refreshInsights = async () => {
      loadingTimer = window.setTimeout(() => {
        if (active) setIsLoading(true);
      }, 200);
      try {
        const response = await fetch("/api/we0/articles/upsert", {
          cache: "no-store",
        });
        if (!response.ok) return;
        const result = (await response.json()) as {
          ok: boolean;
          data?: ArticleListing[];
        };
        if (active && result.ok && Array.isArray(result.data)) {
          setInsights((current) =>
            JSON.stringify(current) === JSON.stringify(result.data)
              ? current
              : result.data!,
          );
        }
      } catch {
        // Keep the last successfully loaded article list.
      } finally {
        window.clearTimeout(loadingTimer);
        if (active) setIsLoading(false);
      }
    };

    void refreshInsights();

    return () => {
      active = false;
      window.clearTimeout(loadingTimer);
    };
  }, []);

  return (
    <>
      <PageHero
        light
        eyebrow="Growth insights"
        title="品牌增长、产品出海与 SEO/GEO 洞察"
        description="围绕企业品牌增长、产品出海、官网与独立站、内容资产、SEO 与 GEO，分享来自真实业务场景的判断与方法。"
        image="/assets/SEO与GEO优化图.png"
      />
      {featuredInsight && (
        <section className="section-shell section-block-sm">
          <article className="featured-insight">
            <div
              className="featured-insight-media"
              style={
                featuredInsight.coverImageUrl
                  ? { backgroundImage: `url(${featuredInsight.coverImageUrl})` }
                  : undefined
              }
            />
            <div className="featured-insight-copy">
              <span className="eyebrow">精选文章</span>
              <h2>{featuredInsight.title}</h2>
              {featuredInsight.summary && <p>{featuredInsight.summary}</p>}
              <span className="text-link">
                <Clock3 size={15} />
                {new Intl.DateTimeFormat("zh-CN", { dateStyle: "long" }).format(
                  new Date(featuredInsight.publishedAt),
                )}
              </span>
              <a
                className="button button-dark"
                href={`/insights/${featuredInsight.slug}`}
              >
                阅读文章 <ArrowUpRight size={16} />
              </a>
            </div>
          </article>
        </section>
      )}
      <section
        id="articles"
        className="insights-section section-shell section-block-sm"
        aria-busy={isLoading}
      >
        <SectionHeading
          title="最新洞察"
          description="从企业真实问题出发，讨论品牌如何被理解、产品如何被发现，以及客户渠道如何持续积累。"
        />
        <div className="insight-loading-slot">
          {isLoading && (
            <div className="insight-loading" role="status" aria-live="polite">
              <Spinner aria-hidden="true" focusable="false" />
              <span>正在获取最新文章</span>
            </div>
          )}
        </div>
        <InsightFilter
          key={currentPage}
          publishedInsights={visibleInsights}
        />
        <Pagination className="insight-pagination">
            <PaginationContent>
              <PaginationItem>
                <PaginationLink
                  href="#articles"
                  aria-label="上一页"
                  aria-disabled={currentPage === 1}
                  tabIndex={currentPage === 1 ? -1 : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    setCurrentPage((page) => Math.max(1, page - 1));
                  }}
                >
                  <ChevronLeft />
                </PaginationLink>
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                (page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      href="#articles"
                      aria-label={`第 ${page} 页`}
                      isActive={page === currentPage}
                      onClick={(event) => {
                        event.preventDefault();
                        setCurrentPage(page);
                      }}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                ),
              )}
              <PaginationItem>
                <PaginationLink
                  href="#articles"
                  aria-label="下一页"
                  aria-disabled={currentPage === totalPages}
                  tabIndex={currentPage === totalPages ? -1 : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    setCurrentPage((page) => Math.min(totalPages, page + 1));
                  }}
                >
                  <ChevronRight />
                </PaginationLink>
              </PaginationItem>
            </PaginationContent>
        </Pagination>
      </section>
      <CTA
        title="获取增长洞察，持续校准品牌与市场动作"
        description="关注品牌增长、产品出海、网站阵地、内容搜索与企业获客的实践方法。"
      />
    </>
  );
}
